let map;
let markers = [];
let infoWindow;

/**
 * Parses theme-entered coordinates; supports comma as decimal separator.
 */
function parseCoord(value) {
  if (value == null || value === '') return NaN;
  const normalized = String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/,/g, '.');
  return parseFloat(normalized);
}

/**
 * Builds an absolute URL for marker icons so Maps can load them reliably.
 */
function resolveMarkerIconUrl(raw) {
  const url = (raw || '').trim();
  if (!url) return null;
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    if (url.startsWith('/')) return `${window.location.origin}${url}`;
    return new URL(url, window.location.href).href;
  } catch {
    return null;
  }
}

/**
 * Drops a stale map instance when the theme editor swaps the section DOM.
 */
function resetMapIfDetached() {
  if (!map || !map.getDiv) return;
  const div = map.getDiv();
  if (div && div.isConnected) return;
  markers.forEach((m) => {
    m.setMap(null);
  });
  markers = [];
  map = null;
}

/**
 * Returns the map marker for a store block id, or null if not found.
 */
function findMarkerByStoreId(storeId) {
  if (storeId == null || storeId === '') return null;
  const idStr = String(storeId);
  return (
    markers.find(
      (m) => m.storeData && String(m.storeData.id) === idStr
    ) || null
  );
}

function initStoresMapInstance() {
  const storesMapContainer = document.querySelector('.stores-map');
  if (!storesMapContainer) return;

  const mapElement = document.getElementById('stores-map');
  if (!mapElement) return;

  resetMapIfDetached();
  if (map) return;

  // Get default center from section settings or use default
  const defaultLat = parseCoord(mapElement.dataset.defaultLat) || -11.840376;
  const defaultLng = parseCoord(mapElement.dataset.defaultLng) || -76.9319352;
  const defaultZoom = parseInt(mapElement.dataset.defaultZoom) || 7;

  // Initialize map
  map = new google.maps.Map(mapElement, {
    zoom: defaultZoom,
    center: { lat: defaultLat, lng: defaultLng },
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true
  });

  // Initialize info window
  infoWindow = new google.maps.InfoWindow();

  // Load stores and create markers
  loadStores();

  // Setup event listeners
  setupEventListeners();
}

function loadStores() {
  const storeItems = document.querySelectorAll('.stores-map__store-item');
  const storeList = [];

  storeItems.forEach((item) => {
    const store = {
      id: item.dataset.storeId,
      name: item.querySelector('.stores-map__store-name')?.textContent || '',
      address: item.querySelector('.stores-map__store-address')?.textContent || '',
      lat: parseCoord(item.dataset.lat),
      lng: parseCoord(item.dataset.lng),
      province: item.dataset.province || '',
      city: item.dataset.city || '',
      element: item
    };
    storeList.push(store);
  });

  const mapElement = document.getElementById('stores-map');
  const rawIconSetting = (mapElement?.dataset.markerIcon || '').trim();
  const resolvedIcon = resolveMarkerIconUrl(rawIconSetting);
  let sharedIconConfig = null;
  if (resolvedIcon) {
    sharedIconConfig = {
      url: resolvedIcon,
      scaledSize: new google.maps.Size(40, 40)
    };
  }

  // Create markers
  storeList.forEach((store) => {
    if (isNaN(store.lat) || isNaN(store.lng)) return;

    // ASSUMPTION: Omit `icon` when no valid URL — broken/relative icons often
    // render invisible pins; default red marker is always visible.

    const markerOptions = {
      position: { lat: store.lat, lng: store.lng },
      map: map,
      title: store.name
    };
    if (sharedIconConfig) {
      markerOptions.icon = sharedIconConfig;
    }

    const marker = new google.maps.Marker(markerOptions);

    // Store reference
    marker.storeData = store;
    markers.push(marker);

    // Click on marker
    marker.addListener('click', () => {
      showInfoWindow(marker, store);
      highlightStoreItem(store.id);
    });
  });

}

function setupEventListeners() {
  // Store item clicks
  document.querySelectorAll('.stores-map__store-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      if (e.target.closest('.stores-map__view-more-btn')) return;
      
      const lat = parseCoord(item.dataset.lat);
      const lng = parseCoord(item.dataset.lng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter(lat, lng, 15);
        highlightStoreItem(item.dataset.storeId);
      }
    });
  });

  // View more buttons
  document.querySelectorAll('.stores-map__view-more-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const lat = parseCoord(btn.dataset.lat);
      const lng = parseCoord(btn.dataset.lng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter(lat, lng, 16);
        
        const storeData = {
          id: btn.dataset.storeId,
          name: btn.dataset.name,
          address: btn.dataset.address,
          complement: btn.dataset.complement,
          hours: btn.dataset.hours,
          phone: btn.dataset.phone,
          lat: lat,
          lng: lng
        };
        
        const marker = findMarkerByStoreId(btn.dataset.storeId);
        showInfoWindow(marker, storeData);
        highlightStoreItem(btn.dataset.storeId);
      }
    });
  });
}

function setMapCenter(lat, lng, zoom = 15) {
  if (!map) return;
  
  map.setCenter({ lat, lng });
  if (zoom) {
    map.setZoom(zoom);
  }
}

function showInfoWindow(marker, storeData) {
  if (!infoWindow || !map) return;

  const infoContent = document.getElementById('info-window-content');
  if (!infoContent) return;

  // ASSUMPTION: One shared InfoWindow instance; close before re-opening so the
  // previous dialog (role="dialog") is torn down — avoids stacked overlays when
  // switching between marker-anchored vs position-only opens.
  infoWindow.close();

  // Update info window content
  const nameEl = document.getElementById('info-name');
  const addressEl = document.getElementById('info-address');
  const complementEl = document.getElementById('info-complement');
  const hoursEl = document.getElementById('info-hours');

  if (nameEl) nameEl.textContent = storeData.name || '';
  if (addressEl) addressEl.textContent = storeData.address || '';
  if (complementEl) {
    complementEl.textContent = storeData.complement || '';
    complementEl.style.display = storeData.complement ? 'block' : 'none';
  }
  if (hoursEl) hoursEl.textContent = storeData.hours || '';

  // Clone and show content
  const content = infoContent.cloneNode(true);
  content.style.display = 'block';
  content.removeAttribute('id');

  infoWindow.setContent(content);

  if (marker) {
    infoWindow.open(map, marker);
  } else if (storeData.lat != null && storeData.lng != null) {
    const position = {
      lat: parseFloat(storeData.lat),
      lng: parseFloat(storeData.lng)
    };
    if (!isNaN(position.lat) && !isNaN(position.lng)) {
      infoWindow.setPosition(position);
      infoWindow.open(map);
    }
  }
}

function highlightStoreItem(storeId) {
  // Remove previous highlights
  document.querySelectorAll('.stores-map__store-item').forEach((item) => {
    item.classList.remove('stores-map__store-item--active');
  });

  // Highlight selected
  const selectedItem = document.querySelector(`[data-store-id="${storeId}"]`);
  if (selectedItem) {
    selectedItem.classList.add('stores-map__store-item--active');
    selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

// Make function globally available for Google Maps callback
window.initStoresMapInstance = initStoresMapInstance;

// If Google Maps is already loaded, initialize immediately
if (typeof google !== 'undefined' && google.maps && document.readyState === 'complete') {
  initStoresMapInstance();
} else if (typeof google !== 'undefined' && google.maps) {
  document.addEventListener('DOMContentLoaded', initStoresMapInstance);
}

